import {
  confirmIdentity,
  verifyInvitation,
} from './api.js';
import { restoreAssessment, restoreSubmission } from './assessment/api.js';
import { startAssessment } from './assessment/app.js';
import { createDeliveryPoller } from './assessment/delivery-poller.js';
import {
  confirmationView,
  entryView,
  loadingView,
  submittedView,
  updateSubmissionDeliveryView,
} from './views.js';
import './styles.css';

const app = document.querySelector('#app');
const MESSAGES = {
  invalid: "That code doesn't match an active invitation. Check it and try again.",
  expired: 'This invitation is no longer active. Please contact the leadership team.',
  rate_limited: 'Too many unsuccessful attempts. Please wait 15 minutes and try again.',
  unavailable: "We couldn't check your invitation just now. Your code has not been used. Please try again.",
};

let state = {
  code: '',
  candidate: null,
};
let assessment = null;
const deliveryPoller = createDeliveryPoller({
  readStatus: restoreSubmission,
  onSent: ({ delivery }) => updateSubmissionDeliveryView(app, delivery),
});

function stopAssessment() {
  deliveryPoller.stop();
  assessment?.destroy();
  assessment = null;
}

function mount(view) {
  app.replaceChildren(view.root);
  return view;
}

function focusAfterPaint(element) {
  requestAnimationFrame(() => element?.focus());
}

function showEntry({ error = '', busy = false, focus = 'input' } = {}) {
  stopAssessment();
  const view = mount(entryView({ code: state.code, error, busy }));

  view.input.addEventListener('input', (event) => {
    state.code = event.currentTarget.value;
    view.button.disabled = state.code.trim().length === 0;
  });
  view.form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (busy || state.code.trim().length === 0) {
      return;
    }

    showEntry({ busy: true, focus: null });
    try {
      const { data } = await verifyInvitation(state.code);
      if (data.state === 'identity_confirmation') {
        state.code = '';
        state.candidate = data.candidate;
        showConfirmation();
        return;
      }
      if (data.state === 'submitted') {
        state.code = '';
        showSubmitted(data);
        return;
      }

      showEntry({ error: MESSAGES[data.state] || MESSAGES.unavailable, focus: 'alert' });
    } catch {
      showEntry({ error: MESSAGES.unavailable, focus: 'alert' });
    }
  });

  if (focus === 'input' && !busy) {
    focusAfterPaint(view.input);
  } else if (focus === 'alert') {
    focusAfterPaint(view.alert);
  }
}

function showConfirmation({ busy = false, error = '' } = {}) {
  stopAssessment();
  const view = mount(confirmationView({ candidate: state.candidate, busy, error }));

  view.confirm.addEventListener('click', () => finishConfirmation(true));
  view.reject.addEventListener('click', () => finishConfirmation(false));

  if (!busy) {
    focusAfterPaint(error ? view.alert : view.heading);
  }
}

async function finishConfirmation(confirmed) {
  showConfirmation({ busy: true });
  try {
    const confirmation = await confirmIdentity(confirmed);
    if (confirmation.status === 401 && confirmation.data.state === 'invalid_session') {
      state = { code: '', candidate: null };
      history.replaceState(null, '', '/');
      showEntry();
      return;
    }
    if (confirmation.status !== 200) {
      showConfirmation({ error: MESSAGES.unavailable });
      return;
    }

    if (confirmation.data.state === 'submitted') {
      state.code = '';
      state.candidate = confirmation.data.candidate;
      showSubmitted(confirmation.data);
      return;
    }

    if (!confirmed) {
      state = { code: '', candidate: null };
      history.replaceState(null, '', confirmation.data.next || '/');
      showEntry();
      return;
    }

    const restored = await restoreAssessment();
    if (restored.status === 200 && restored.data.state === 'draft') {
      state.candidate = restored.data.candidate;
      history.pushState(null, '', confirmation.data.next);
      await showAssessment(restored.data.candidate, {
        draft: restored.data.draft,
        draftScope: restored.data.draftScope,
      });
      return;
    }
    if (restored.status === 200 && restored.data.state === 'submitted') {
      state.candidate = restored.data.candidate;
      showSubmitted(restored.data);
      return;
    }
    if (restored.status === 401 && restored.data.state === 'invalid_session') {
      state = { code: '', candidate: null };
      history.replaceState(null, '', '/');
      showEntry();
      return;
    }

    showConfirmation({ error: MESSAGES.unavailable });
  } catch {
    showConfirmation({ error: MESSAGES.unavailable });
  }
}

function showSubmitted({ candidate, firstName, delivery = 'pending', submittedAt = null } = {}) {
  stopAssessment();
  const submittedCandidate = candidate || state.candidate || { firstName };
  const details = {
    firstName: submittedCandidate?.firstName || firstName,
    delivery,
    submittedAt,
  };
  state.candidate = submittedCandidate;
  history.replaceState(null, '', '/assessment/confirmation');
  const view = mount(submittedView(details));
  focusAfterPaint(view.heading);
  if (delivery === 'pending') deliveryPoller.start();
}

function returnToEntry() {
  state = { code: '', candidate: null };
  history.replaceState(null, '', '/');
  showEntry();
}

async function showAssessment(candidate, { draft = {}, draftScope } = {}) {
  stopAssessment();
  assessment = await startAssessment({
    root: app,
    candidate,
    draft,
    draftScope,
    onInvalidSession: returnToEntry,
    onSubmitted: showSubmitted,
  });
}

async function restoreProtectedRoute() {
  mount(loadingView());
  try {
    const { status, data } = await restoreAssessment();
    if (status === 200 && data.state === 'draft') {
      state.candidate = data.candidate;
      await showAssessment(data.candidate, {
        draft: data.draft,
        draftScope: data.draftScope,
      });
      return;
    }
    if (status === 200 && data.state === 'submitted') {
      showSubmitted(data);
      return;
    }
  } catch {
    state.code = '';
    history.replaceState(null, '', '/');
    showEntry({ error: MESSAGES.unavailable, focus: 'alert' });
    return;
  }

  state = { code: '', candidate: null };
  history.replaceState(null, '', '/');
  showEntry();
}

function start() {
  if (/^\/assessment\/[a-z0-9-]+\/?$/u.test(window.location.pathname)) {
    if (assessment && state.candidate) {
      assessment.navigate(window.location.pathname.split('/').filter(Boolean).at(-1), {
        updateHistory: false,
      });
      return;
    }
    restoreProtectedRoute();
  } else {
    showEntry();
  }
}

window.addEventListener('popstate', start);
start();
